/**
 * `AdminScoringSettings` — pagina admin per configurare i parametri di calcolo
 * dei punteggi (rotta `/admin/scoring`).
 *
 * Espone i parametri globali del motore di scoring, letti/salvati via
 * `/api/settings/scoring`:
 *  - pesi delle quattro fonti di voto (self, AI, reviewer, manager);
 *  - fattore di aderenza per ogni distanza di Livello (la distanza 0 vale
 *    sempre 100% ed è mostrata staticamente, non editabile);
 *  - smoothing bayesiano del rank (m e prior);
 *  - soglia di qualificazione (dashboard "Livelli");
 *  - peso di importanza di default di una skill.
 *
 * La scrittura è consentita solo all'`admin`: l'autorizzazione autorevole è
 * dell'API (403 per i non-admin). La configurazione è globale (una sola per
 * l'intera azienda).
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_PUBLIC_CV_PRIVACY_POLICY_URL,
  DEFAULT_SCORING_CONFIG,
  LEVEL_DISTANCE_COUNT,
  MAX_SKILL_WEIGHT,
  MIN_SKILL_WEIGHT,
  SKILL_TYPE_LABELS,
  SKILL_TYPE_VALUES,
  type AiSettingsDto,
  type ApiKeyDto,
  type BedrockModelDto,
  type CreateApiKeyResponse,
  type ListAiModelsResponse,
  type PublicCvSettingsDto,
  type ScoringConfigDto,
  type SkillType,
  type UpdateAiSettingsRequest,
  type UpdatePublicCvSettingsRequest,
  type UpdateScoringConfigRequest,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { ImageUploadField } from '../components/ImageUploadField';
import { ApiRestGuideContent } from './AdminApiRestGuide';
import { ApiMcpGuideContent } from './AdminApiMcpGuide';
import {
  Alert,
  Button,
  Card,
  ChipAutocomplete,
  DataTable,
  Field,
  Loading,
  Icon,
  Modal,
  PageHeader,
  TextArea,
  TextInput,
  type ChipOption,
  type Column,
  type IconName,
} from '../components/ui';

const scoringKey = ['settings', 'scoring'] as const;
const publicCvKey = ['settings', 'publicCv'] as const;
const aiKey = ['settings', 'ai'] as const;
const aiModelsKey = ['settings', 'ai', 'models'] as const;

/**
 * Opzioni del `ChipAutocomplete` dei tipi di skill da estrarre: i tre valori di
 * `SkillType` con la relativa etichetta, nell'ordine canonico di
 * `SKILL_TYPE_VALUES`.
 */
const SKILL_TYPE_OPTIONS: readonly ChipOption[] = SKILL_TYPE_VALUES.map((type) => ({
  id: type,
  name: SKILL_TYPE_LABELS[type],
}));

/**
 * Verifica che `value` sia un URL http(s) valido e non vuoto.
 * L'API resta autorevole; questa è una validazione difensiva lato client.
 */
function isValidHttpUrl(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '') return false;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * `usePublicCvSettings` — logica della sezione "Pagina pubblica" (informativa
 * privacy del flusso di upload CV pubblico, `/api/settings/public-cv`).
 *
 * Tenuta **separata** dallo scoring: query, stato locale e salvataggio sono
 * indipendenti. Esporre lo stato tramite hook consente di rendere le azioni
 * (Ripristina / Salva) nel `PageHeader` insieme al form nel corpo pagina.
 */
function usePublicCvSettings() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: publicCvKey,
    queryFn: () => api.get<PublicCvSettingsDto>('/api/settings/public-cv'),
  });

  // Input controllati inizializzati dal valore recuperato dal server.
  // `privacyPolicyUrl === null` funge da sentinella di "non ancora
  // inizializzato" per l'intero form (entrambi i campi arrivano dalla stessa
  // query); `logoUrl` può legittimamente restare stringa vuota (nessun logo).
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [backgroundUrl, setBackgroundUrl] = useState<string>('');
  const [primaryColor, setPrimaryColor] = useState<string>('');

  useEffect(() => {
    if (settingsQuery.data && privacyPolicyUrl === null) {
      setPrivacyPolicyUrl(settingsQuery.data.privacyPolicyUrl);
      setLogoUrl(settingsQuery.data.logoUrl);
      setBackgroundUrl(settingsQuery.data.backgroundUrl);
      setPrimaryColor(settingsQuery.data.primaryColor);
    }
  }, [settingsQuery.data, privacyPolicyUrl]);

  const saveMutation = useMutation({
    mutationFn: (body: UpdatePublicCvSettingsRequest) =>
      api.put<PublicCvSettingsDto>('/api/settings/public-cv', body),
    onSuccess: (saved) => {
      queryClient.setQueryData(publicCvKey, saved);
      setPrivacyPolicyUrl(saved.privacyPolicyUrl);
      setLogoUrl(saved.logoUrl);
      setBackgroundUrl(saved.backgroundUrl);
      setPrimaryColor(saved.primaryColor);
    },
  });

  const validationError = useMemo<string | null>(() => {
    if (privacyPolicyUrl === null) return null;
    if (!isValidHttpUrl(privacyPolicyUrl)) {
      return "L'URL dell'informativa privacy deve essere un indirizzo http(s) valido.";
    }
    // Colore opzionale: se valorizzato deve essere un esadecimale valido.
    if (primaryColor.trim() !== '' && !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(primaryColor.trim())) {
      return 'Il colore primario deve essere un valore esadecimale (es. #C63968).';
    }
    return null;
  }, [privacyPolicyUrl, primaryColor]);

  const handleSubmit = () => {
    if (privacyPolicyUrl === null || validationError !== null) return;
    saveMutation.mutate({
      privacyPolicyUrl: privacyPolicyUrl.trim(),
      logoUrl: logoUrl.trim(),
      backgroundUrl: backgroundUrl.trim(),
      primaryColor: primaryColor.trim(),
    });
  };

  // Il "Ripristina default" riporta l'informativa al valore di default e
  // azzera logo, sfondo e colore (torna al form pubblico "vergine").
  const resetToDefault = () => {
    setPrivacyPolicyUrl(DEFAULT_PUBLIC_CV_PRIVACY_POLICY_URL);
    setLogoUrl('');
    setBackgroundUrl('');
    setPrimaryColor('');
  };

  return {
    settingsQuery,
    privacyPolicyUrl,
    setPrivacyPolicyUrl,
    logoUrl,
    setLogoUrl,
    backgroundUrl,
    setBackgroundUrl,
    primaryColor,
    setPrimaryColor,
    saveMutation,
    validationError,
    handleSubmit,
    resetToDefault,
  };
}

type PublicCvSettings = ReturnType<typeof usePublicCvSettings>;

/**
 * Azioni della sezione "Pagina pubblica", rese nel `PageHeader`. Il pulsante
 * "Salva" invia il form tramite l'attributo `form="public-cv-form"`, così può
 * vivere fuori dal `<form>`.
 */
function PublicCvSettingsActions({ state }: { state: PublicCvSettings }) {
  const { saveMutation, validationError, resetToDefault, privacyPolicyUrl } = state;
  const disabled = privacyPolicyUrl === null;
  return (
    <div className="admin-toolbar">
      <Button type="button" variant="ghost" onClick={resetToDefault} disabled={disabled}>
        Ripristina default
      </Button>
      <Button
        type="submit"
        form="public-cv-form"
        variant="primary"
        loading={saveMutation.isPending}
        disabled={disabled || validationError !== null}
      >
        Salva
      </Button>
    </div>
  );
}

/**
 * `PublicPageUrlField` — input in sola lettura con l'URL assoluto della pagina
 * pubblica di candidatura (`/public/submission`) e un pulsante icona per
 * copiarlo negli appunti. L'URL è derivato dall'origin corrente, così riflette
 * l'ambiente (dev/prod) senza configurazione.
 */
function PublicPageUrlField() {
  const publicUrl = `${window.location.origin}/public/submission`;
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard non disponibile (es. contesto non sicuro): nessuna azione.
    }
  };

  return (
    <div className="public-url-field">
      <TextInput
        id="public-cv-url"
        type="text"
        value={publicUrl}
        disabled
        aria-label="URL della pagina pubblica di candidatura"
      />
      <Button
        type="button"
        variant="ghost"
        iconOnly
        onClick={handleCopy}
        aria-label={copied ? 'Copiato' : 'Copia URL'}
        title={copied ? 'Copiato' : 'Copia URL'}
      >
        <Icon name={copied ? 'check' : 'copy'} title={copied ? 'Copiato' : 'Copia URL'} />
      </Button>
    </div>
  );
}

/**
 * `PublicCvSettingsCard` — corpo della sezione "Pagina pubblica" (senza le
 * azioni Ripristina/Salva, che sono rese nel `PageHeader`).
 */
function PublicCvSettingsCard({ state }: { state: PublicCvSettings }) {
  const api = useApiClient();
  const {
    settingsQuery,
    privacyPolicyUrl,
    setPrivacyPolicyUrl,
    logoUrl,
    setLogoUrl,
    backgroundUrl,
    setBackgroundUrl,
    primaryColor,
    setPrimaryColor,
    validationError,
    saveMutation,
    handleSubmit,
  } = state;

  if (settingsQuery.isLoading || privacyPolicyUrl === null) {
    return (
      <Card>
        <h2 className="scoring-admin__section-title">Pagina pubblica di candidatura</h2>
        <Loading>Caricamento impostazioni…</Loading>
      </Card>
    );
  }

  if (settingsQuery.isError) {
    return (
      <Card>
        <h2 className="scoring-admin__section-title">Pagina pubblica di candidatura</h2>
        <Alert tone="error">Impossibile caricare le impostazioni della pagina pubblica. Riprova.</Alert>
      </Card>
    );
  }

  return (
    <Card>
      <h2 className="scoring-admin__section-title">Pagina pubblica di candidatura</h2>
      <p className="scoring-admin__section-help">
        Personalizza la pagina pubblica dove i candidati esterni inviano il
        proprio CV senza autenticarsi. Le impostazioni qui sotto valgono per tutti
        i visitatori del link pubblico.
      </p>
      <PublicPageUrlField />
      <form
        id="public-cv-form"
        className="admin-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {/* Logo */}
        <div>
          <h3 className="scoring-admin__section-title">Logo</h3>
          <p className="scoring-admin__section-help">
            Immagine mostrata centrata in cima al form pubblico. Formati
            supportati: PNG, JPEG, WEBP, GIF o SVG. Lascia vuoto per non mostrare
            alcun logo.
          </p>
          <ImageUploadField
            value={logoUrl}
            onChange={setLogoUrl}
            api={api}
            label="Carica logo"
          />
        </div>

        {/* Sfondo */}
        <div>
          <h3 className="scoring-admin__section-title">Immagine di sfondo</h3>
          <p className="scoring-admin__section-help">
            Immagine mostrata come sfondo della pagina pubblica. Formati
            supportati: PNG, JPEG, WEBP, GIF o SVG. Lascia vuoto per usare lo
            sfondo di default.
          </p>
          <ImageUploadField
            value={backgroundUrl}
            onChange={setBackgroundUrl}
            api={api}
            label="Carica sfondo"
          />
        </div>

        {/* Colore primario di accent */}
        <div>
          <h3 className="scoring-admin__section-title">Colore primario</h3>
          <p className="scoring-admin__section-help">
            Colore di accent usato per pulsanti e link della pagina pubblica.
            Lascia vuoto per usare il colore di default del tema.
          </p>
          <div className="scoring-admin__grid">
            <Field
              id="public-cv-primary-color"
              label="Colore primario"
              hint="Formato esadecimale, es. #C63968"
            >
              <div className="public-cv-color-field">
                <input
                  id="public-cv-primary-color-picker"
                  type="color"
                  className="public-cv-color-field__picker"
                  aria-label="Selettore colore primario"
                  value={primaryColor.trim() === '' ? '#000000' : primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                />
                <TextInput
                  id="public-cv-primary-color"
                  type="text"
                  placeholder="#C63968"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                />
                {primaryColor.trim() !== '' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setPrimaryColor('')}
                  >
                    Rimuovi
                  </Button>
                )}
              </div>
            </Field>
          </div>
        </div>

        {/* Informativa privacy */}
        <div>
          <h3 className="scoring-admin__section-title">Informativa privacy</h3>
          <p className="scoring-admin__section-help">
            Indirizzo dell'informativa sulla privacy mostrata e sottoscritta nel
            form pubblico. Deve essere un URL http(s) valido.
          </p>
          <div className="scoring-admin__grid">
            <Field
              id="public-cv-privacy-url"
              label="URL informativa privacy"
              hint="Es. https://esempio.it/informativa-privacy.pdf"
            >
              <TextInput
                id="public-cv-privacy-url"
                type="url"
                value={privacyPolicyUrl}
                onChange={(e) => setPrivacyPolicyUrl(e.target.value)}
              />
            </Field>
          </div>
        </div>

        {validationError !== null && <Alert tone="error">{validationError}</Alert>}
        {saveMutation.isError && (
          <Alert tone="error">Impossibile salvare le impostazioni della pagina pubblica. Riprova.</Alert>
        )}
        {saveMutation.isSuccess && !saveMutation.isPending && (
          <Alert tone="success">Impostazioni della pagina pubblica salvate.</Alert>
        )}
      </form>
    </Card>
  );
}

/**
 * `useAiSettings` — logica della sezione "AI" (configurazione dei due flussi
 * Bedrock: pre-compilazione CV e generatore di quiz, `/api/settings/ai`).
 *
 * Tenuta **separata** dallo scoring e dalla pagina pubblica: query, stato
 * locale e salvataggio sono indipendenti. I valori ricevuti dal server sono
 * già i default effettivi quando nulla è stato salvato (prompt discorsivi
 * attuali, tutti i tipi skill, modello `BEDROCK_MODEL_ID`), quindi il
 * "Ripristina default" riporta ai valori iniziali ricevuti dalla query.
 */
function useAiSettings() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: aiKey,
    queryFn: () => api.get<AiSettingsDto>('/api/settings/ai'),
  });

  // Input controllati inizializzati dal valore recuperato dal server.
  // `initialized === false` funge da sentinella di "non ancora inizializzato"
  // per l'intero form (tutti i campi arrivano dalla stessa query); i prompt
  // possono legittimamente restare stringa vuota (= usa il default del modulo).
  const [initialized, setInitialized] = useState(false);
  const [cvExtractionPrompt, setCvExtractionPrompt] = useState('');
  const [cvExtractionModelId, setCvExtractionModelId] = useState('');
  const [cvAllowedSkillTypes, setCvAllowedSkillTypes] = useState<Set<SkillType>>(
    () => new Set(),
  );
  const [quizPrompt, setQuizPrompt] = useState('');
  const [quizModelId, setQuizModelId] = useState('');

  // Applica i valori ricevuti dal server allo stato locale controllato.
  const applyServerValues = (data: AiSettingsDto) => {
    setCvExtractionPrompt(data.cvExtractionPrompt);
    setCvExtractionModelId(data.cvExtractionModelId);
    setCvAllowedSkillTypes(new Set(data.cvAllowedSkillTypes));
    setQuizPrompt(data.quizPrompt);
    setQuizModelId(data.quizModelId);
  };

  useEffect(() => {
    if (settingsQuery.data && !initialized) {
      applyServerValues(settingsQuery.data);
      setInitialized(true);
    }
  }, [settingsQuery.data, initialized]);

  const saveMutation = useMutation({
    mutationFn: (body: UpdateAiSettingsRequest) =>
      api.put<AiSettingsDto>('/api/settings/ai', body),
    onSuccess: (saved) => {
      queryClient.setQueryData(aiKey, saved);
      applyServerValues(saved);
    },
  });

  // Sostituisce l'insieme dei tipi ammessi con quelli scelti nel
  // `ChipAutocomplete`. Filtra sui valori noti (difensivo) preservando l'ordine
  // canonico, così lo stato resta un sottoinsieme valido di `SkillType`.
  const setAllowedSkillTypes = (ids: readonly string[]) =>
    setCvAllowedSkillTypes(
      new Set(SKILL_TYPE_VALUES.filter((t) => ids.includes(t))),
    );

  const validationError = useMemo<string | null>(() => {
    if (!initialized) return null;
    // I prompt sono opzionali: se valorizzati non devono essere solo spazi
    // (la stringa vuota è la sentinella "usa il default del modulo").
    if (cvExtractionPrompt.length > 0 && cvExtractionPrompt.trim() === '') {
      return 'Il prompt di estrazione CV non può contenere solo spazi.';
    }
    if (quizPrompt.length > 0 && quizPrompt.trim() === '') {
      return 'Il prompt del generatore di quiz non può contenere solo spazi.';
    }
    if (cvAllowedSkillTypes.size === 0) {
      return 'Seleziona almeno un tipo di skill da iniettare nel prompt di estrazione.';
    }
    if (cvExtractionModelId.trim() === '') {
      return "L'id del modello di estrazione CV è obbligatorio.";
    }
    if (quizModelId.trim() === '') {
      return "L'id del modello del generatore di quiz è obbligatorio.";
    }
    return null;
  }, [
    initialized,
    cvExtractionPrompt,
    quizPrompt,
    cvAllowedSkillTypes,
    cvExtractionModelId,
    quizModelId,
  ]);

  const handleSubmit = () => {
    if (!initialized || validationError !== null) return;
    // Ordine canonico dei tipi skill (coerente con SKILL_TYPE_VALUES).
    const allowed = SKILL_TYPE_VALUES.filter((t) => cvAllowedSkillTypes.has(t));
    saveMutation.mutate({
      cvExtractionPrompt: cvExtractionPrompt.trim(),
      cvExtractionModelId: cvExtractionModelId.trim(),
      cvAllowedSkillTypes: allowed,
      quizPrompt: quizPrompt.trim(),
      quizModelId: quizModelId.trim(),
    });
  };

  // Il "Ripristina default" riporta ai valori iniziali ricevuti dal server,
  // che sono già i default effettivi quando nulla è stato salvato.
  const resetToDefault = () => {
    if (settingsQuery.data) applyServerValues(settingsQuery.data);
  };

  return {
    settingsQuery,
    initialized,
    cvExtractionPrompt,
    setCvExtractionPrompt,
    cvExtractionModelId,
    setCvExtractionModelId,
    cvAllowedSkillTypes,
    setAllowedSkillTypes,
    quizPrompt,
    setQuizPrompt,
    quizModelId,
    setQuizModelId,
    saveMutation,
    validationError,
    handleSubmit,
    resetToDefault,
  };
}

type AiSettings = ReturnType<typeof useAiSettings>;

/**
 * Azioni della sezione "AI", rese nel `PageHeader`. Il pulsante "Salva" invia
 * il form tramite l'attributo `form="ai-form"`, così può vivere fuori dal
 * `<form>`.
 */
function AiSettingsActions({ state }: { state: AiSettings }) {
  const { saveMutation, validationError, resetToDefault, initialized } = state;
  return (
    <div className="admin-toolbar">
      <Button type="button" variant="ghost" onClick={resetToDefault} disabled={!initialized}>
        Ripristina default
      </Button>
      <Button
        type="submit"
        form="ai-form"
        variant="primary"
        loading={saveMutation.isPending}
        disabled={!initialized || validationError !== null}
      >
        Salva
      </Button>
    </div>
  );
}

/**
 * `useAiModels` — query dell'elenco degli inference profile Bedrock
 * selezionabili (`GET /api/settings/ai/models`), usato per popolare entrambi i
 * selettori dei modelli (estrazione CV e quiz): la lista è la STESSA per i due.
 *
 * L'endpoint degrada senza errore HTTP (restituisce `ok: false, models: []`)
 * quando AWS non è raggiungibile: la query resta quindi "di successo" ma con
 * elenco vuoto, e i selettori ricadono sul solo valore corrente/di default
 * (sempre reso selezionabile da {@link ModelSelect}).
 */
function useAiModels() {
  const api = useApiClient();
  return useQuery({
    queryKey: aiModelsKey,
    queryFn: () => api.get<ListAiModelsResponse>('/api/settings/ai/models'),
  });
}

type AiModelsQuery = ReturnType<typeof useAiModels>;

/**
 * Mappatura prefisso del model id tecnico → etichetta leggibile del fornitore.
 * Il fornitore si deriva dal token prima del primo `.` nel `technicalModelId`
 * (o, in mancanza, nell'`id` del profilo dopo il prefisso `eu.`). I prefissi
 * non previsti ricadono su una capitalizzazione del token (vedi
 * {@link providerLabelFromPrefix}).
 */
const PROVIDER_LABELS: Record<string, string> = {
  amazon: 'Amazon',
  anthropic: 'Anthropic',
  meta: 'Meta',
  mistral: 'Mistral',
  cohere: 'Cohere',
  ai21: 'AI21 Labs',
  deepseek: 'DeepSeek',
  stability: 'Stability AI',
  writer: 'Writer',
  twelvelabs: 'TwelveLabs',
  luma: 'Luma',
};

/** Etichetta usata per i modelli di cui non si riesce a dedurre il fornitore. */
const UNKNOWN_PROVIDER_LABEL = 'Altri';

/** Etichetta del gruppo che contiene il valore corrente/salvato fuori-lista. */
const CURRENT_MODEL_GROUP_LABEL = 'Modello attuale';

/**
 * Deriva il token del fornitore (prefisso) da un modello: usa il
 * `technicalModelId` se presente (token prima del primo `.`), altrimenti
 * ricava il token dall'`id` del profilo togliendo il prefisso geografico `eu.`.
 * Ritorna `null` se non deducibile.
 */
function providerPrefix(model: BedrockModelDto): string | null {
  const technical = model.technicalModelId?.trim();
  if (technical !== undefined && technical !== '') {
    const token = technical.split('.')[0]?.trim();
    if (token !== undefined && token !== '') return token.toLowerCase();
  }
  // Ripiego sull'id del profilo: `eu.<provider>.<model>` → `<provider>`.
  const withoutGeo = model.id.replace(/^eu\./i, '');
  const token = withoutGeo.split('.')[0]?.trim();
  if (token !== undefined && token !== '') return token.toLowerCase();
  return null;
}

/**
 * Etichetta leggibile del fornitore a partire dal prefisso: usa
 * {@link PROVIDER_LABELS} se noto, altrimenti capitalizza il token (fallback
 * sensato per fornitori non previsti). `null` → {@link UNKNOWN_PROVIDER_LABEL}.
 */
function providerLabelFromPrefix(prefix: string | null): string {
  if (prefix === null) return UNKNOWN_PROVIDER_LABEL;
  const known = PROVIDER_LABELS[prefix];
  if (known !== undefined) return known;
  return prefix.charAt(0).toUpperCase() + prefix.slice(1);
}

/**
 * Etichetta completa di un'opzione: `"<name> (<technicalModelId>)"`. Se il
 * `technicalModelId` non è ricavabile si mostra solo il `name`.
 */
function modelOptionLabel(model: BedrockModelDto): string {
  const technical = model.technicalModelId?.trim();
  return technical !== undefined && technical !== ''
    ? `${model.name} (${technical})`
    : model.name;
}

/** Gruppo di opzioni per il raggruppamento `optgroup`, ordinato per fornitore. */
interface ModelOptionGroup {
  label: string;
  models: BedrockModelDto[];
}

/**
 * Raggruppa i modelli per fornitore (prefisso del model id tecnico) e ordina in
 * modo stabile: i gruppi per etichetta di fornitore, con il gruppo "Altri"
 * sempre in coda; dentro ciascun gruppo le opzioni per etichetta leggibile.
 */
function groupModelsByProvider(models: BedrockModelDto[]): ModelOptionGroup[] {
  const byLabel = new Map<string, BedrockModelDto[]>();
  for (const model of models) {
    const label = providerLabelFromPrefix(providerPrefix(model));
    const bucket = byLabel.get(label) ?? [];
    bucket.push(model);
    byLabel.set(label, bucket);
  }

  const groups: ModelOptionGroup[] = [...byLabel.entries()].map(
    ([label, groupModels]) => ({
      label,
      models: [...groupModels].sort((a, b) =>
        modelOptionLabel(a).localeCompare(modelOptionLabel(b)),
      ),
    }),
  );

  // Ordine stabile dei gruppi per etichetta, con "Altri" sempre in coda.
  groups.sort((a, b) => {
    if (a.label === UNKNOWN_PROVIDER_LABEL) return 1;
    if (b.label === UNKNOWN_PROVIDER_LABEL) return -1;
    return a.label.localeCompare(b.label);
  });
  return groups;
}

/**
 * `ModelSelect` — `<select>` nativo (vestito con lo stile pill `.ui-select`
 * comune agli altri select admin) per scegliere un modello Bedrock.
 *
 * Le opzioni sono raggruppate per fornitore in `<optgroup>` (derivato dal
 * prefisso del model id tecnico); l'etichetta di ciascuna opzione è
 * `"<name> (<technicalModelId>)"`.
 *
 * Requisito VINCOLANTE: il valore attualmente selezionato (`value`) è SEMPRE
 * presente tra le opzioni, anche se assente dall'elenco restituito da AWS (es.
 * un modello salvato fuori-lista o il default `BEDROCK_MODEL_ID`): in tal caso
 * viene aggiunto in un gruppo dedicato "Modello attuale", così non sparisce
 * mai. Durante il caricamento o in caso di elenco vuoto il select resta usabile
 * con il solo valore corrente.
 */
function ModelSelect({
  id,
  value,
  onChange,
  models,
  loading,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  models: BedrockModelDto[];
  loading: boolean;
}) {
  // Il valore corrente deve sempre essere un'opzione: se l'elenco AWS non lo
  // contiene (fuori-lista o default non elencato), lo si mette in un gruppo
  // dedicato "Modello attuale", mostrando l'id come etichetta (nessun nome
  // leggibile disponibile).
  const currentInList = value.trim() !== '' && models.some((m) => m.id === value);
  const groups = groupModelsByProvider(models);
  return (
    <span className="ui-select">
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {value.trim() !== '' && !currentInList && (
          <optgroup label={CURRENT_MODEL_GROUP_LABEL}>
            <option value={value}>{value}</option>
          </optgroup>
        )}
        {groups.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.models.map((m) => (
              <option key={m.id} value={m.id}>
                {modelOptionLabel(m)}
              </option>
            ))}
          </optgroup>
        ))}
        {loading && models.length === 0 && value.trim() === '' && (
          <option value="">Caricamento modelli…</option>
        )}
      </select>
    </span>
  );
}

/**
 * `AiSettingsCard` — corpo della sezione "AI" (senza le azioni
 * Ripristina/Salva, che sono rese nel `PageHeader`).
 */
function AiSettingsCard({ state, modelsQuery }: { state: AiSettings; modelsQuery: AiModelsQuery }) {
  const {
    settingsQuery,
    initialized,
    cvExtractionPrompt,
    setCvExtractionPrompt,
    cvExtractionModelId,
    setCvExtractionModelId,
    cvAllowedSkillTypes,
    setAllowedSkillTypes,
    quizPrompt,
    setQuizPrompt,
    quizModelId,
    setQuizModelId,
    validationError,
    saveMutation,
    handleSubmit,
  } = state;

  // Elenco dei modelli per entrambi i selettori: lista unica, stessa per
  // estrazione CV e quiz. Il fallback (`ok: false`) o l'elenco vuoto non rompono
  // il form: `ModelSelect` mantiene comunque selezionabile il valore corrente.
  const models = modelsQuery.data?.models ?? [];
  const modelsLoading = modelsQuery.isLoading;
  // `false` solo quando AWS ha risposto esplicitamente con un errore gestito
  // (fallback): si mostra un avviso non bloccante accanto ai selettori.
  const modelsDegraded = modelsQuery.data?.ok === false;

  // Blocco introduttivo: SOLO titolo e descrizione generica della sezione
  // (nessun campo), reso in cima sia negli stati di caricamento/errore sia nel
  // form, così l'intestazione resta coerente.
  const introBlock = (
    <>
      <h2 className="scoring-admin__section-title">Impostazioni AI</h2>
      <p className="scoring-admin__section-help">
        Configura i due flussi AI basati su Amazon Bedrock: la pre-compilazione
        delle skill dal CV e il generatore di quiz per la valutazione AI. Lascia
        un prompt vuoto per usare il testo di default del sistema.
      </p>
    </>
  );

  if (settingsQuery.isLoading || !initialized) {
    return (
      <Card>
        {introBlock}
        <Loading>Caricamento impostazioni…</Loading>
      </Card>
    );
  }

  if (settingsQuery.isError) {
    return (
      <Card>
        {introBlock}
        <Alert tone="error">Impossibile caricare le impostazioni AI. Riprova.</Alert>
      </Card>
    );
  }

  return (
    <>
      {/* Blocco 1 — intestazione introduttiva: solo titolo + descrizione. */}
      <Card>{introBlock}</Card>
      <form
        id="ai-form"
        className="admin-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {/* Blocco 2 — Estrazione CV: prompt + modello + tipi skill (il filtro
            per tipo riguarda l'estrazione, quindi vive in questo blocco). */}
        <Card>
          <h3 className="scoring-admin__section-title">Estrazione CV</h3>
          <p className="scoring-admin__section-help">
            Estrazione automatica delle skill dal CV. Puoi personalizzare la
            parte discorsiva del prompt (ruolo e regole di riconoscimento): i
            blocchi tecnici (scala dei Livelli, formato JSON) restano gestiti dal
            sistema.
          </p>
          <div className="scoring-admin__fields">
            <Field
              id="ai-cv-extraction-prompt"
              label="Prompt di estrazione CV"
              hint="Lascia vuoto per usare il prompt di default del sistema."
            >
              <TextArea
                id="ai-cv-extraction-prompt"
                rows={8}
                value={cvExtractionPrompt}
                onChange={(e) => setCvExtractionPrompt(e.target.value)}
              />
            </Field>
            <Field
              id="ai-cv-extraction-model"
              label="Modello di estrazione CV"
              hint="Modello Bedrock usato per l'estrazione, scelto tra gli inference profile disponibili."
            >
              <ModelSelect
                id="ai-cv-extraction-model"
                value={cvExtractionModelId}
                onChange={setCvExtractionModelId}
                models={models}
                loading={modelsLoading}
              />
            </Field>
            <Field
              id="ai-cv-skill-types"
              label="Tipi di skill da estrarre"
              hint="Solo le skill dei tipi selezionati vengono iniettate nel prompt di estrazione e considerate nel match automatico."
            >
              <ChipAutocomplete
                id="ai-cv-skill-types"
                options={SKILL_TYPE_OPTIONS}
                selectedIds={[...cvAllowedSkillTypes]}
                onChange={setAllowedSkillTypes}
                placeholder="Aggiungi un tipo di skill…"
                emptyMessage="Nessun altro tipo disponibile."
              />
            </Field>
          </div>
        </Card>

        {/* Blocco 3 — Generazione quiz: prompt + modello. */}
        <Card>
          <h3 className="scoring-admin__section-title">Generazione quiz</h3>
          <p className="scoring-admin__section-help">
            Valutazione AI delle skill tramite quiz. Puoi personalizzare la parte
            discorsiva del prompt (persona e modo di costruire le domande): i
            blocchi tecnici (conteggi, scala dei Livelli, formato JSON) restano
            gestiti dal sistema.
          </p>
          <div className="scoring-admin__fields">
            <Field
              id="ai-quiz-prompt"
              label="Prompt del generatore di quiz"
              hint="Lascia vuoto per usare il prompt di default del sistema."
            >
              <TextArea
                id="ai-quiz-prompt"
                rows={8}
                value={quizPrompt}
                onChange={(e) => setQuizPrompt(e.target.value)}
              />
            </Field>
            <Field
              id="ai-quiz-model"
              label="Modello del generatore di quiz"
              hint="Modello Bedrock usato per il quiz, scelto tra gli inference profile disponibili."
            >
              <ModelSelect
                id="ai-quiz-model"
                value={quizModelId}
                onChange={setQuizModelId}
                models={models}
                loading={modelsLoading}
              />
            </Field>
          </div>
        </Card>

        {modelsDegraded && (
          <Alert tone="warning">
            Impossibile recuperare l'elenco dei modelli da AWS: resta
            selezionabile solo il modello attualmente salvato. Riprova più tardi.
          </Alert>
        )}
        {validationError !== null && <Alert tone="error">{validationError}</Alert>}
        {saveMutation.isError && (
          <Alert tone="error">Impossibile salvare le impostazioni AI. Riprova.</Alert>
        )}
        {saveMutation.isSuccess && !saveMutation.isPending && (
          <Alert tone="success">Impostazioni AI salvate.</Alert>
        )}
      </form>
    </>
  );
}

/**
 * Stato del form: tutti i valori sono stringhe (input controllati), così da
 * gestire input parziali e decimali senza forzare `NaN`. I fattori di distanza
 * sono espressi in **percentuale** (0..100) per l'utente e riconvertiti in
 * frazione `[0,1]` al salvataggio.
 */
interface FormState {
  self: string;
  ai: string;
  reviewer: string;
  manager: string;
  /** Percentuali (0..100) per le distanze 1..LEVEL_DISTANCE_COUNT. */
  distancePercents: string[];
  rankSmoothingM: string;
  rankSmoothingPrior: string;
  qualifiedRankThreshold: string;
  defaultSkillWeight: string;
  /** Bonus percentuale (0..100) per ogni certificazione valida collegata a una skill. */
  certificationBonus: string;
}

/** Converte una `ScoringConfigDto` nello stato del form (numeri → stringhe). */
function toFormState(config: ScoringConfigDto): FormState {
  return {
    self: String(config.sourceWeights.self),
    ai: String(config.sourceWeights.ai),
    reviewer: String(config.sourceWeights.reviewer),
    manager: String(config.sourceWeights.manager),
    distancePercents: config.levelDistanceFactors.map((f) =>
      String(Math.round(f * 100)),
    ),
    rankSmoothingM: String(config.rankSmoothingM),
    rankSmoothingPrior: String(config.rankSmoothingPrior),
    qualifiedRankThreshold: String(config.qualifiedRankThreshold),
    defaultSkillWeight: String(config.defaultSkillWeight),
    certificationBonus: String(config.certificationBonus),
  };
}

/** Etichetta di una distanza `d` (es. "Distanza 2"). */
function distanceLabel(d: number): string {
  return `Distanza ${d}`;
}

/** Parsa una stringa numerica; restituisce `NaN` se non valida. */
function num(value: string): number {
  const trimmed = value.trim();
  if (trimmed === '') return NaN;
  return Number(trimmed);
}

/**
 * Voci del menù laterale del pannello di amministrazione. Ogni voce mostra una
 * sezione distinta del form, così da spezzettare la pagina in blocchi più
 * gestibili.
 */
type AdminTab = 'scoring' | 'publicCv' | 'ai' | 'apiKeys' | 'apiRest' | 'apiMcp';

const ADMIN_TABS: ReadonlyArray<{ id: AdminTab; label: string; icon?: IconName }> = [
  { id: 'scoring', label: 'Scoring' },
  { id: 'publicCv', label: 'Pagina pubblica' },
  // Sezione delle impostazioni AI, evidenziata dall'icona "sparkle".
  { id: 'ai', label: 'AI', icon: 'sparkle' },
  { id: 'apiKeys', label: 'API keys' },
  // Guide di sola consultazione alle API, rese come sezioni del menù laterale.
  { id: 'apiRest', label: 'Documentazione API' },
  { id: 'apiMcp', label: 'MCP' },
];

export default function AdminScoringSettings() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  // Sezione attiva del menù laterale.
  const [activeTab, setActiveTab] = useState<AdminTab>('scoring');

  // Stato della sezione "Pagina pubblica" (indipendente dallo scoring).
  const publicCv = usePublicCvSettings();

  // Stato della sezione "AI" (indipendente dalle altre sezioni).
  const ai = useAiSettings();
  // Elenco dei modelli Bedrock per i selettori del form AI (lista condivisa).
  const aiModels = useAiModels();

  const configQuery = useQuery({
    queryKey: scoringKey,
    queryFn: () => api.get<ScoringConfigDto>('/api/settings/scoring'),
  });

  const [form, setForm] = useState<FormState | null>(null);

  // Inizializza il form quando arriva la configurazione dal server.
  useEffect(() => {
    if (configQuery.data && form === null) {
      setForm(toFormState(configQuery.data));
    }
  }, [configQuery.data, form]);

  const saveMutation = useMutation({
    mutationFn: (body: UpdateScoringConfigRequest) =>
      api.put<ScoringConfigDto>('/api/settings/scoring', body),
    onSuccess: (saved) => {
      queryClient.setQueryData(scoringKey, saved);
      setForm(toFormState(saved));
    },
  });

  const update = (patch: Partial<FormState>) =>
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));

  const updateDistance = (index: number, value: string) =>
    setForm((prev) => {
      if (!prev) return prev;
      const next = [...prev.distancePercents];
      next[index] = value;
      return { ...prev, distancePercents: next };
    });

  // Validazione client (difensiva; l'API resta autorevole). Restituisce il
  // primo messaggio di errore, oppure `null` se il form è valido.
  const validationError = useMemo<string | null>(() => {
    if (!form) return null;

    const weights = [
      { v: num(form.self), label: 'Peso self' },
      { v: num(form.ai), label: 'Peso AI' },
      { v: num(form.reviewer), label: 'Peso reviewer' },
      { v: num(form.manager), label: 'Peso manager' },
    ];
    for (const { v, label } of weights) {
      if (Number.isNaN(v) || v < 0) return `${label} deve essere un numero >= 0.`;
    }
    if (weights.reduce((s, w) => s + w.v, 0) <= 0) {
      return 'Almeno un peso delle fonti deve essere maggiore di zero.';
    }

    for (let i = 0; i < form.distancePercents.length; i += 1) {
      const p = num(form.distancePercents[i]);
      if (Number.isNaN(p) || p < 0 || p > 100) {
        return `Il fattore per la distanza ${i + 1} deve essere una percentuale tra 0 e 100.`;
      }
    }

    const m = num(form.rankSmoothingM);
    if (Number.isNaN(m) || m < 0) return 'Lo smoothing (m) deve essere un numero >= 0.';
    const prior = num(form.rankSmoothingPrior);
    if (Number.isNaN(prior) || prior < 0 || prior > 10)
      return 'Il prior dello smoothing deve essere tra 0 e 10.';
    const threshold = num(form.qualifiedRankThreshold);
    if (Number.isNaN(threshold) || threshold < 0 || threshold > 100)
      return 'La soglia di qualificazione deve essere tra 0 e 100.';
    const weight = num(form.defaultSkillWeight);
    if (
      Number.isNaN(weight) ||
      !Number.isInteger(weight) ||
      weight < MIN_SKILL_WEIGHT ||
      weight > MAX_SKILL_WEIGHT
    )
      return `Il peso skill di default deve essere un intero tra ${MIN_SKILL_WEIGHT} e ${MAX_SKILL_WEIGHT}.`;

    const bonus = num(form.certificationBonus);
    if (Number.isNaN(bonus) || bonus < 0 || bonus > 100)
      return 'Il bonus certificazioni deve essere una percentuale tra 0 e 100.';

    return null;
  }, [form]);

  const handleSubmit = () => {
    if (!form || validationError !== null) return;
    const body: UpdateScoringConfigRequest = {
      sourceWeights: {
        self: num(form.self),
        ai: num(form.ai),
        reviewer: num(form.reviewer),
        manager: num(form.manager),
      },
      axisWeights: DEFAULT_SCORING_CONFIG.axisWeights,
      minScore: DEFAULT_SCORING_CONFIG.minScore,
      // Percentuali (0..100) → frazioni [0,1].
      levelDistanceFactors: form.distancePercents.map((p) => num(p) / 100),
      rankSmoothingM: num(form.rankSmoothingM),
      rankSmoothingPrior: num(form.rankSmoothingPrior),
      qualifiedRankThreshold: num(form.qualifiedRankThreshold),
      defaultSkillWeight: num(form.defaultSkillWeight),
      certificationBonus: num(form.certificationBonus),
    };
    saveMutation.mutate(body);
  };

  const resetToDefaults = () => setForm(toFormState(DEFAULT_SCORING_CONFIG));

  if (configQuery.isLoading || form === null) {
    return (
      <section aria-labelledby="scoring-admin-title">
        <PageHeader
          title="Amministrazione"
          titleId="scoring-admin-title"
        />
        <Loading>Caricamento configurazione…</Loading>
      </section>
    );
  }

  if (configQuery.isError) {
    return (
      <section aria-labelledby="scoring-admin-title">
        <PageHeader
          title="Amministrazione"
          titleId="scoring-admin-title"
        />
        <Alert tone="error">Impossibile caricare la configurazione. Riprova.</Alert>
      </section>
    );
  }

  return (
    <section className="scoring-admin" aria-labelledby="scoring-admin-title">
      <PageHeader
        title="Amministrazione"
        titleId="scoring-admin-title"
        subtitle="Configura i parametri di calcolo dei punteggi e le impostazioni della pagina pubblica. La configurazione è globale."
        actions={
          activeTab === 'scoring' ? (
            <div className="admin-toolbar">
              <Button type="button" variant="ghost" onClick={resetToDefaults}>
                Ripristina default
              </Button>
              <Button
                type="submit"
                form="scoring-form"
                variant="primary"
                loading={saveMutation.isPending}
                disabled={validationError !== null}
              >
                Salva
              </Button>
            </div>
          ) : activeTab === 'publicCv' ? (
            <PublicCvSettingsActions state={publicCv} />
          ) : activeTab === 'ai' ? (
            <AiSettingsActions state={ai} />
          ) : undefined
        }
      />

      <div className="admin-layout">
        {/* Menù laterale a colonna: spezza il form in sezioni distinte. */}
        <nav className="admin-layout__nav" aria-label="Sezioni di amministrazione">
          <ul className="admin-layout__nav-list">
            {ADMIN_TABS.map((tab) => (
              <li
                key={tab.id}
                // La prima voce di sola consultazione ("API keys") apre un
                // gruppo visivamente staccato dalle sezioni operative
                // soprastanti (spazio + linea sopra il list item, così il
                // pulsante resta identico alle altre voci).
                className={
                  tab.id === 'apiKeys' ? 'admin-layout__nav-group-start' : undefined
                }
              >
                <button
                  type="button"
                  className={[
                    'admin-layout__nav-item',
                    activeTab === tab.id ? 'admin-layout__nav-item--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.icon && <Icon name={tab.icon} />}
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Corpo: mostra solo la sezione selezionata. */}
        <div className="admin-layout__body">
          <form
            id="scoring-form"
            className="admin-form"
            hidden={activeTab !== 'scoring'}
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >

        {/* Pesi delle fonti di voto */}
        <Card>
          <h2 className="scoring-admin__section-title">Pesi delle fonti di voto</h2>
          <p className="scoring-admin__section-help">
            Peso relativo di ogni fonte nella media di una skill. Una fonte assente
            non incide: la media è ricalcolata sulle sole fonti presenti.
          </p>
          <div className="scoring-admin__grid">
            <Field id="weight-self" label="Autovalutazione (self)">
              <TextInput
                id="weight-self"
                type="number"
                min={0}
                step="0.5"
                value={form.self}
                onChange={(e) => update({ self: e.target.value })}
              />
            </Field>
            <Field id="weight-ai" label="Valutazione AI">
              <TextInput
                id="weight-ai"
                type="number"
                min={0}
                step="0.5"
                value={form.ai}
                onChange={(e) => update({ ai: e.target.value })}
              />
            </Field>
            <Field id="weight-reviewer" label="Reviewer">
              <TextInput
                id="weight-reviewer"
                type="number"
                min={0}
                step="0.5"
                value={form.reviewer}
                onChange={(e) => update({ reviewer: e.target.value })}
              />
            </Field>
            <Field id="weight-manager" label="Manager">
              <TextInput
                id="weight-manager"
                type="number"
                min={0}
                step="0.5"
                value={form.manager}
                onChange={(e) => update({ manager: e.target.value })}
              />
            </Field>
          </div>
        </Card>

        {/* Impatto della distanza di Livello */}
        <Card>
          <h2 className="scoring-admin__section-title">Impatto della distanza di Livello</h2>
          <p className="scoring-admin__section-help">
            Quanto vale il voto in base alla differenza tra il Livello richiesto
            dalla competenza e quello dichiarato. Chi è sotto il Livello richiesto
            non contribuisce (0%). A distanza 0 il voto vale sempre il 100%.
          </p>
          <div className="scoring-admin__grid scoring-admin__grid--distances">
            <Field id="distance-0" label="Distanza 0" hint="Livello esatto">
              <TextInput id="distance-0" type="number" value={100} disabled readOnly />
            </Field>
            {Array.from({ length: LEVEL_DISTANCE_COUNT }, (_, i) => (
              <Field
                key={i}
                id={`distance-${i + 1}`}
                label={distanceLabel(i + 1)}
                hint="Percentuale del voto (0–100)"
              >
                <TextInput
                  id={`distance-${i + 1}`}
                  type="number"
                  min={0}
                  max={100}
                  step="1"
                  value={form.distancePercents[i] ?? ''}
                  onChange={(e) => updateDistance(i, e.target.value)}
                />
              </Field>
            ))}
          </div>
        </Card>

        {/* Altre variabili di calcolo */}
        <Card>
          <h2 className="scoring-admin__section-title">Altre variabili di calcolo</h2>
          <div className="scoring-admin__grid scoring-admin__grid--cols-3">
            <Field
              id="qualified-threshold"
              label="Soglia di qualificazione"
              hint="Punteggio (0–100) oltre il quale una persona è “qualificata” su una competenza."
            >
              <TextInput
                id="qualified-threshold"
                type="number"
                min={0}
                max={100}
                step="1"
                value={form.qualifiedRankThreshold}
                onChange={(e) => update({ qualifiedRankThreshold: e.target.value })}
              />
            </Field>
            <Field
              id="default-skill-weight"
              label="Peso skill di default"
              hint={`Intero ${MIN_SKILL_WEIGHT}–${MAX_SKILL_WEIGHT} usato quando una skill non ha un peso esplicito.`}
            >
              <TextInput
                id="default-skill-weight"
                type="number"
                min={MIN_SKILL_WEIGHT}
                max={MAX_SKILL_WEIGHT}
                step="1"
                value={form.defaultSkillWeight}
                onChange={(e) => update({ defaultSkillWeight: e.target.value })}
              />
            </Field>
            <Field
              id="certification-bonus"
              label="Bonus certificazioni"
              hint="Percentuale (0–100) aggiunta allo Score di una skill per ogni certificazione valida collegata. Cumulativo: lo Score può superare 10 (mostrato come “10+”)."
            >
              <TextInput
                id="certification-bonus"
                type="number"
                min={0}
                max={100}
                step="1"
                value={form.certificationBonus}
                onChange={(e) => update({ certificationBonus: e.target.value })}
              />
            </Field>
          </div>
          <div className="scoring-admin__grid scoring-admin__grid--cols-2">
            <Field
              id="smoothing-m"
              label="Affidabilità del match (m)"
              hint="Più alto = i match basati su poche skill valutate pesano meno."
            >
              <TextInput
                id="smoothing-m"
                type="number"
                min={0}
                step="1"
                value={form.rankSmoothingM}
                onChange={(e) => update({ rankSmoothingM: e.target.value })}
              />
            </Field>
            <Field
              id="smoothing-prior"
              label="Valore iniziale del match (prior)"
              hint="Punteggio di partenza (0–10) attribuito alle skill non ancora valutate."
            >
              <TextInput
                id="smoothing-prior"
                type="number"
                min={0}
                max={10}
                step="0.5"
                value={form.rankSmoothingPrior}
                onChange={(e) => update({ rankSmoothingPrior: e.target.value })}
              />
            </Field>
          </div>
        </Card>

            {validationError !== null && <Alert tone="error">{validationError}</Alert>}
            {saveMutation.isError && (
              <Alert tone="error">Impossibile salvare la configurazione. Riprova.</Alert>
            )}
            {saveMutation.isSuccess && !saveMutation.isPending && (
              <Alert tone="success">Configurazione salvata.</Alert>
            )}
          </form>

          {/* Sezione indipendente: informativa privacy del flusso CV pubblico. */}
          <div hidden={activeTab !== 'publicCv'}>
            <PublicCvSettingsCard state={publicCv} />
          </div>

          {/* Sezione indipendente: impostazioni dei flussi AI (Bedrock). La
              classe di stacking (gap verticale uniforme tra la card intro e il
              form sottostante) è applicata solo quando il tab è attivo: così il
              `display:flex` dello stack non annulla l'attributo `hidden` quando
              la sezione non è selezionata. */}
          <div
            hidden={activeTab !== 'ai'}
            className={activeTab === 'ai' ? 'admin-section-stack' : undefined}
          >
            <AiSettingsCard state={ai} modelsQuery={aiModels} />
          </div>

          {/* Sezione indipendente: gestione delle API key. */}
          <div hidden={activeTab !== 'apiKeys'}>
            <ApiKeysSection />
          </div>

          {/* Guide di sola consultazione: API REST e server MCP. */}
          <div hidden={activeTab !== 'apiRest'}>
            <ApiRestGuideContent />
          </div>
          <div hidden={activeTab !== 'apiMcp'}>
            <ApiMcpGuideContent />
          </div>
        </div>
      </div>
    </section>
  );
}

const apiKeysKey = ['api-keys'] as const;

/** Formatta una data ISO in `gg/mm/aaaa, hh:mm`, o placeholder se assente. */
function formatDateTime(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Sezione "API keys": lista delle chiavi con
 * creazione (la chiave in chiaro è mostrata una sola volta) ed eliminazione.
 */
function ApiKeysSection() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  /** Chiave appena creata, mostrata una sola volta (o `null`). */
  const [createdKey, setCreatedKey] = useState<CreateApiKeyResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const keysQuery = useQuery({
    queryKey: apiKeysKey,
    queryFn: () => api.get<ApiKeyDto[]>('/api/api-keys'),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: apiKeysKey });
  };

  const createKey = useMutation({
    mutationFn: (label: string) =>
      api.post<CreateApiKeyResponse>('/api/api-keys', { label }),
    onSuccess: (result) => {
      setCreatedKey(result);
      setNewLabel('');
      setIsCreateOpen(false);
      setCopied(false);
      invalidate();
    },
  });

  const deleteKey = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/api-keys/${id}`),
    onSuccess: () => invalidate(),
  });

  const keys = keysQuery.data ?? [];
  const trimmedLabel = newLabel.trim();

  const copyKey = async () => {
    if (createdKey === null) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
    } catch {
      // Clipboard non disponibile: l'utente può copiare manualmente dal campo.
      setCopied(false);
    }
  };

  const columns = useMemo<Column<ApiKeyDto>[]>(
    () => [
      {
        key: 'label',
        header: 'Etichetta',
        sortable: true,
        sortAccessor: (row) => row.label.toLowerCase(),
        render: (row) => <span>{row.label}</span>,
      },
      {
        key: 'prefix',
        header: 'Chiave',
        render: (row) => <code className="api-key-prefix">{row.keyPrefix}…</code>,
      },
      {
        key: 'createdAt',
        header: 'Creata il',
        sortable: true,
        sortAccessor: (row) => row.createdAt,
        render: (row) => formatDateTime(row.createdAt),
      },
      {
        key: 'lastUsedAt',
        header: 'Ultimo uso',
        sortable: true,
        sortAccessor: (row) => row.lastUsedAt ?? '',
        render: (row) => formatDateTime(row.lastUsedAt),
      },
      {
        key: 'actions',
        header: 'Azioni',
        className: 'admin-table__actions-col',
        render: (row) => (
          <Button
            type="button"
            size="sm"
            variant="danger"
            iconOnly
            aria-label={`Revoca la API key ${row.label}`}
            title={`Revoca ${row.label}`}
            onClick={() => deleteKey.mutate(row.id)}
            loading={deleteKey.isPending}
          >
            <Icon name="trash" />
          </Button>
        ),
      },
    ],
    [deleteKey],
  );

  return (
    <div className="admin-form">
      <Card>
        <div className="scoring-admin__section-header">
          <h2 className="scoring-admin__section-title">Chiavi</h2>
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              setNewLabel('');
              setIsCreateOpen(true);
            }}
          >
            Nuova API key
          </Button>
        </div>

        {/* Chiave appena creata: mostrata una sola volta, con copia. */}
        {createdKey !== null && (
          <Alert tone="success">
            <p>
              Chiave <strong>{createdKey.apiKey.label}</strong> creata. Copiala
              adesso: non sarà più visibile in seguito.
            </p>
            <div className="api-key-reveal">
              <TextInput
                id="api-key-reveal"
                readOnly
                value={createdKey.key}
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button type="button" onClick={() => void copyKey()}>
                {copied ? 'Copiata' : 'Copia'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setCreatedKey(null)}
              >
                Chiudi
              </Button>
            </div>
          </Alert>
        )}

        {keysQuery.isLoading ? (
          <Loading>Caricamento API key…</Loading>
        ) : keysQuery.isError ? (
          <Alert tone="error">Impossibile caricare le API key.</Alert>
        ) : (
          <DataTable
            columns={columns}
            rows={keys}
            rowKey={(row) => row.id}
            defaultSort={{ key: 'createdAt', direction: 'desc' }}
            emptyMessage="Nessuna API key. Creane una nuova per usare le API pubbliche."
          />
        )}
      </Card>

      <Modal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Nuova API key"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setIsCreateOpen(false)}>
              Annulla
            </Button>
            <Button
              type="submit"
              form="api-key-create-form"
              variant="primary"
              loading={createKey.isPending}
              disabled={trimmedLabel.length === 0}
            >
              Crea
            </Button>
          </>
        }
      >
        <form
          id="api-key-create-form"
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmedLabel.length === 0) return;
            createKey.mutate(trimmedLabel);
          }}
        >
          <Field
            id="api-key-label"
            label="Etichetta"
            hint="Un nome per riconoscere la chiave (es. “Integrazione HR”)."
          >
            <TextInput
              id="api-key-label"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Es. Integrazione HR"
            />
          </Field>
          {createKey.isError && (
            <Alert tone="error">Impossibile creare la API key. Riprova.</Alert>
          )}
        </form>
      </Modal>
    </div>
  );
}
